import {
  apiUtil,
  useDebouncedSearch,
  useGetProjectsQueryState,
  useInfiniteScroll,
  useLazyGetProjectsQuery,
  useMyUser,
  useWriteQueryParam,
} from '@consigli/hooks';
import { type GetProjectsResponse, Project } from '@consigli/types';
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router';

export interface UseProjectListResult {
  /** Projects loaded so far, accumulated across pages. */
  projects: Project[];
  /** True during the first page load, and while a new search resolves. */
  isLoading: boolean;
  /** True while a subsequent page is being appended. */
  isFetchingMore: boolean;
  /** True between a keystroke and the debounced search firing. */
  isSearchPending: boolean;
  /** Current (undebounced) contents of the search box. */
  searchText: string;
  handleSearch: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Which creator filter is active. Deliberately not persisted. */
  creatorFilter: CreatorFilter;
  setCreatorFilter: (filter: CreatorFilter) => void;
  /** Attach to a sentinel element at the end of the list. */
  triggerRef: ReturnType<typeof useInfiniteScroll>;
}

/**
 * `mine` sends the current user's id as `created_by`; `all` sends nothing.
 * "Created by others" needs an exclusion the backend filter cannot express —
 * UUIDInFilter has no negative arm — so it is not an option here yet.
 *
 * The type derives from the constant, so the pill values, the narrowing at the
 * toolbar boundary and this union cannot drift apart.
 */
/** Query params, so a filtered or searched list is a link someone can send. */
const FILTER_PARAM = 'filter';
const SEARCH_PARAM = 'search';

export const CREATOR_FILTER = {
  ALL: 'all',
  MINE: 'mine',
} as const;

export type CreatorFilter = (typeof CREATOR_FILTER)[keyof typeof CREATOR_FILTER];

/**
 * Loads the project list: paged fetching, debounced search, and infinite
 * scroll. Extracted from `project-list-page.tsx` so the page and its redesign
 * can share one data path rather than duplicating it.
 *
 * The first page is requested as soon as the hook mounts. Fetch failures are
 * logged rather than surfaced.
 */
export const useProjectList = (): UseProjectListResult => {
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  // A cached page 1 is showing and its fresh copy is on the way. Paging waits:
  // the fresh page 1 replaces the list, which would drop a page 2 appended now.
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [getProjects] = useLazyGetProjectsQuery();
  const dispatch = useDispatch();
  const { user } = useMyUser();

  const [searchParams] = useSearchParams();
  const writeQueryParam = useWriteQueryParam();
  const urlSearch = searchParams.get(SEARCH_PARAM) ?? '';
  // Seeded from the URL, as the filter below is, so the reset effect does not
  // read a return visit to `?search=` as a query change and clear its cards.
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(urlSearch);

  // Derived, not stored: no debounce sits in front of it, so there is nothing to
  // keep in step. Narrowed through CREATOR_FILTER because the URL is a boundary
  // anyone can type into.
  const creatorFilter: CreatorFilter =
    searchParams.get(FILTER_PARAM) === CREATOR_FILTER.MINE
      ? CREATOR_FILTER.MINE
      : CREATOR_FILTER.ALL;

  const [currentCreatorFilter, setCurrentCreatorFilter] = useState<CreatorFilter>(creatorFilter);

  // Pushes, so back undoes a filter change. Search replaces instead — see below.
  const setCreatorFilter = useCallback(
    (filter: CreatorFilter) => {
      writeQueryParam(FILTER_PARAM, filter === CREATOR_FILTER.MINE ? filter : '');
    },
    [writeQueryParam],
  );

  // Bumped whenever the query changes. A page-2 response that resolves after a
  // filter switch belongs to the previous query and is dropped rather than
  // appended — the debounce hides this for search, but pills fire on click.
  const requestGeneration = useRef(0);
  // The request in flight, so a query change can abort it. Each page costs the
  // server seconds, and the generation check alone only hides the old response.
  const inFlight = useRef<{ abort: () => void } | null>(null);

  // Seeded in the initialiser, not an effect. useDebouncedSearch initialises its
  // own value to its input, so seeding here makes the first render already carry
  // the URL's term — one fetch with the right params. From an effect it would be
  // two, with a flash of the unfiltered list between them.
  const [searchText, setSearchText] = useState(urlSearch);
  const debouncedSearchText = useDebouncedSearch(searchText, 500);

  const pageSize = 50;

  // One builder for every page, so the cache read below and the fetches key the
  // same RTK entry.
  const pageQuery = useCallback(
    (page: number) => ({
      page,
      pageSize,
      search: debouncedSearchText,
      isHidden: false,
      createdBy: creatorFilter === CREATOR_FILTER.MINE ? user.id : undefined,
      // Sorted by the same field the cards display. Ordering by anything
      // else leaves the visible "Updated x ago" values looking shuffled,
      // since the order is then set by a value nobody can see.
      ordering: '-updated_at',
    }),
    [debouncedSearchText, creatorFilter, user.id],
  );

  // A return visit's page 1, read during the first render. fetchProjects draws
  // from the cache too, but after an await in an effect, which runs after the
  // first paint: that frame showed the skeleton, and the cards then replaced it.
  const { currentData: cachedFirstPage } = useGetProjectsQueryState(pageQuery(1));
  const [projects, setProjects] = useState<Project[]>(() => cachedFirstPage?.results ?? []);
  const [isLoading, setIsLoading] = useState<boolean>(() => cachedFirstPage === undefined);
  const [hasNextPage, setHasNextPage] = useState<boolean>(() =>
    cachedFirstPage === undefined ? true : Boolean(cachedFirstPage.next),
  );

  // What we last wrote, so the effect below can tell a navigation from an echo
  // of our own write. Without it the two effects fight: both fire on exactly the
  // same condition.
  const lastWrittenSearch = useRef(urlSearch);

  // Replace, not push: pushing per keystroke fills the history with fragments.
  // The trade is that back never undoes a search, it jumps to the previous
  // filter change.
  useEffect(() => {
    if (debouncedSearchText !== lastWrittenSearch.current) {
      lastWrittenSearch.current = debouncedSearchText;
      writeQueryParam(SEARCH_PARAM, debouncedSearchText, { replace: true });
    }
  }, [debouncedSearchText, writeQueryParam]);

  // The URL moved without us — a back press, or a pasted link. Push it down into
  // the input, which otherwise keeps showing the term from the entry we left.
  useEffect(() => {
    if (urlSearch !== lastWrittenSearch.current) {
      lastWrittenSearch.current = urlSearch;
      setSearchText(urlSearch);
    }
  }, [urlSearch]);

  const isSearchPending = searchText !== debouncedSearchText;

  // Reset pagination when the query changes — search term or creator filter.
  // Without the filter here currentPage survives the switch, so the next
  // infinite-scroll tick asks for page 5 of a freshly filtered list and leaves
  // a hole in it.
  useEffect(() => {
    if (debouncedSearchText !== currentSearchTerm || creatorFilter !== currentCreatorFilter) {
      setCurrentSearchTerm(debouncedSearchText);
      setCurrentCreatorFilter(creatorFilter);
      setCurrentPage(1);
      setProjects([]);
      // Here rather than in fetchProjects, so a return visit painted from the
      // cache does not drop back to the skeleton when its refresh starts.
      setIsLoading(true);
      setHasNextPage(true);
      setIsRefreshing(false);
      requestGeneration.current += 1;
      inFlight.current?.abort();
      inFlight.current = null;
    }
  }, [debouncedSearchText, currentSearchTerm, creatorFilter, currentCreatorFilter]);

  const fetchProjects = useCallback(
    async (page: number, isInitialLoad: boolean = false) => {
      if (!isInitialLoad) {
        setIsFetchingMore(true);
      }

      const generation = requestGeneration.current;

      const query = pageQuery(page);

      const show = (result: GetProjectsResponse) => {
        setHasNextPage(!!result.next);

        // A list row is the same serialized project the detail endpoint
        // returns, so the project page draws from it at once. The page still
        // refetches on mount, so the seed is shown, never trusted.
        dispatch(
          apiUtil.upsertQueryEntries(
            result.results.map((project) => ({
              endpointName: 'getProject' as const,
              arg: { projectId: project.id },
              value: project,
            })),
          ),
        );

        if (page === 1) {
          setProjects(result.results);
        } else {
          setProjects((prevProjects) => [...prevProjects, ...result.results]);
        }
      };

      try {
        // Page 1 draws from RTK's cache when it has the page, then asks for it
        // again regardless, so a return visit shows the list at once and the
        // fresh rows replace it as soon as they land. With nothing cached the
        // first call is itself the request, and there is no second one.
        if (page === 1) {
          const requestedAt = Date.now();
          const cachedRequest = getProjects(query, true);
          inFlight.current = cachedRequest;
          const cached = await cachedRequest;
          if (generation !== requestGeneration.current) return;

          // Started by this call, so it is the request itself. RTK stamps the
          // start on every request, a failed one included; it sets
          // fulfilledTimeStamp only on success.
          const isFresh = (cached.startedTimeStamp ?? 0) >= requestedAt;
          if (cached.isError && isFresh) throw cached.error;
          if (cached.data) {
            show(cached.data);
            setIsLoading(false);
          }
          if (isFresh) return;
          setIsRefreshing(true);
        }

        const request = getProjects(query);
        inFlight.current = request;
        const result = await request.unwrap();

        // The query moved on while this was in flight; its rows are not ours.
        if (generation !== requestGeneration.current) {
          return;
        }

        show(result);
      } catch (error) {
        // An old query's failure, most often the abort above, is not the user's.
        if (generation !== requestGeneration.current) return;
        console.error('Error fetching projects:', error);
      } finally {
        // Only this request's own flags. Clearing them from a stale response
        // would turn off a spinner belonging to one still in flight, and an
        // empty list with isLoading false is what renders the empty state.
        //
        // It cannot strand the spinner: the generation only moves when the
        // search term or the creator filter changed, both of which are in this
        // callback's deps, so the effect always issues a replacement whose
        // finally does match.
        if (generation === requestGeneration.current) {
          setIsLoading(false);
          setIsFetchingMore(false);
          setIsRefreshing(false);
        }
      }
    },
    [getProjects, dispatch, pageQuery],
  );

  // Initial load and search term changes
  useEffect(() => {
    fetchProjects(1, true);
  }, [fetchProjects]);

  // An unmount ends this list's generation. A request left running still fills
  // RTK's cache for the return visit, but its rows no longer reach state or
  // re-seed getProject over a project page that has fetched its own copy.
  useEffect(
    () => () => {
      requestGeneration.current += 1;
    },
    [],
  );

  // Fetch next page function for infinite scroll
  const fetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingMore && !isLoading && !isRefreshing) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchProjects(nextPage, false);
    }
  }, [hasNextPage, isFetchingMore, isLoading, isRefreshing, currentPage, fetchProjects]);

  // Set up infinite scroll
  const triggerRef = useInfiniteScroll({
    hasNextPage,
    // A new observer reports at once whether the sentinel is in view, so paging
    // resumes by itself when the refresh lands.
    isFetchingNextPage: isFetchingMore || isRefreshing,
    fetchNextPage,
    // A page takes seconds to come back, so ask for it well before the end.
    rootMargin: '1500px',
  });

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  return {
    projects,
    isLoading,
    isFetchingMore,
    isSearchPending,
    searchText,
    handleSearch,
    creatorFilter,
    setCreatorFilter,
    triggerRef,
  };
};
